import React, { useEffect, useRef, useState } from "react";

import "./HomePane.scss";

import BaseObject, { globalOnChange } from "../models/BaseObject";
import { useRedraw } from "../models/useRerender";
import { enumAvailableLanguages } from "../models/SettingsEnums";
import Device from "../models/Device";
import { ChannelType, channelTypeInfo } from "../models/ChannelType";
import { itx, noItx } from "../translationItx/itx";

import { isUiModeDeveloper } from "../uiMode";
import OnBoardingElement from "../onBoarding/OnBoardingElement";

function devicesWithoutKnxProduct(): Array<Device> {
  const allDevices = state.allDevices();
  const res = new Array<Device>();

  allDevices.forEach((device) => {
    if (!device.template && !channelTypeInfo(device.channelType).isSupply && device.channelType != ChannelType.Lan) {
      res.push(device);
    }
  });

  return res;
}

function ListOfDevicesWithoutKnxProduct(props: { devices: Array<Device> }) {
  const { devices } = props;

  const rows: any = [];
  devices.forEach((device) => {
    rows.push(
      <tr key={`emptyPane_noKnx_${device.id}`}>
        <td>
          <div
            className="treeMenuIcon"
            data-tooltip-id={"tv-tooltip"}
            data-tooltip-content={state.translate("homePane.openDeviceSettings", "Geräteeigenschafen öffnen")}
            data-tooltip-place="bottom"
            onClick={(ev) => {
              device.treeViewModel.handleOpen();
            }}
          >
            <i className={`las la-edit`}></i>
          </div>
        </td>
        <td>
          <div
            onClick={() => (state.selectionManager.selectedObject = device)}
            data-tooltip-id={"tv-tooltip"}
            data-tooltip-content={state.translate("homePane.navigateToDevice", "Navigire zu diesem Geräte")}
            data-tooltip-place="bottom"
          >
            {noItx(device.fullTag)}
          </div>
        </td>
        <td>
          <div onClick={() => (state.selectionManager.selectedObject = device)}>{noItx(device.name)}</div>
        </td>
      </tr>,
    );
  });

  return (
    <table id="listOfDevicesWithoutKnxProduct" style={{ width: "max-content" }}>
      <tbody style={{ cursor: "pointer" }}>{rows}</tbody>
    </table>
  );
}

export default function HomePane() {
  const onSomeObjectChangedHandler = useRef<any>(0);
  const redraw = useRedraw();
  const [content, setContent] = useState(itx("homePane.contentLoading", "contenting loading ...."));

  const attachEventTimer = useRef<any>(0);

  function onSomeObjectChanged(changedObject: BaseObject, changedProperty: string | any | undefined) {
    if (changedProperty != "select") {
      redraw();
    }
  }

  function onOpenRoom1(selectOnly: boolean) {
    const floor1 = state.floors.sortBy("tag")[0];
    if (floor1) {
      const room1 = floor1.rooms.sortBy("tag")[0];
      if (room1) {
        state.selectionManager.selectedObject = room1;
        if (!selectOnly) {
          room1.treeViewModel.handleOpen();
        }
      }
    }
  }

  useEffect(() => {
    onSomeObjectChangedHandler.current = globalOnChange.subscribe(onSomeObjectChanged);

    let lang = "en";
    if (state.settings.language == enumAvailableLanguages.german) {
      lang = "de";
    }

    if (state.projectMetadata.gptToRunText) {
      // has to bee with timeout ansonsten kommt der bottom tab nicht nach
      setTimeout(() => {
        if (
          state.activePanes.isOpen("ai") // no itx
        ) {
          state.activePanes.activatePane("ai"); // no itx
        } else {
          state.activePanes.loadPage("ai"); // no itx
        }
      }, 200);
    }

    fetch(`/EmptyPaneAssist/${lang}.html`).then((res) => {
      res.text().then((text) => setContent(text));
    });

    if (attachEventTimer.current) {
      clearTimeout(attachEventTimer.current);
    }

    attachEventTimer.current = setTimeout(() => {
      attachEventTimer.current = 0;

      document.getElementById("navigateRoom1")?.addEventListener("click", () => onOpenRoom1(true));

      document.getElementById("openRoom1")?.addEventListener("click", () => onOpenRoom1(false));
    }, 100);

    return () => {
      globalOnChange.unsubscribe(onSomeObjectChangedHandler.current);
      onSomeObjectChangedHandler.current = 0;
    };
  }, []);

  let projectFirstTimeOpen = state.projectMetadata.assistanceData.projectFirstTimeOpen();
  const isDemoProject = state.projectMetadata.assistanceData.isDemoProject();

  const hasFloors = state.floors.size > 0;
  let hasRooms = false;
  let hasConsumers = false;
  let hasButtons = false;
  let isCreatedWithAssitant = false;

  state.floors.forEach((floor) => {
    floor.rooms.forEach((room) => {
      hasRooms = true;
      hasConsumers ||= room.consumers.size > 0;
      hasButtons ||= room.buttons.size > 0;
    });
  });

  const devs = devicesWithoutKnxProduct();

  // DEBUG Setting to style this page
  if (false) {
    hasRooms = false;
  }
  // end DEBUG Setting to style this page

  function addStyle(className: string, visible: boolean) {
    return `.${className} { display: ${visible ? "inherit" : "none"} } \n`;
  }

  if (hasFloors && hasRooms && hasConsumers) {
    projectFirstTimeOpen = false;
  }

  let style = "";
  /////////////////////////////////////////////////////////////////////////////////////////////////////
  // show / hide sections based on project state
  style += addStyle("projectFirstTimeOpen", projectFirstTimeOpen); // no itx
  style += addStyle("isDemoProject", isDemoProject); // no itx
  style += addStyle("needsFloors", !hasFloors); // no itx
  style += addStyle("needsRooms", hasFloors && !hasRooms); // no itx
  style += addStyle("needsConsumers", hasFloors && hasRooms && !hasConsumers); // no itx

  style += addStyle("listOfDevicesWithoutKnxProduct", devs.length > 0); // no itx

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  if (isUiModeDeveloper() && state.taskAndMessageManager.hasConsistencyMessages) {
    return (
      <div
        id="pane_home"
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          paddingTop: "10px",
          paddingBottom: "10px",
          paddingRight: "10px",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div>{itx("HomePane.consistencymessages", "Consistency Messages")}</div>

          {state.taskAndMessageManager.consitencyMessages.map((msg, index) => (
            <div key={index}>{msg}</div>
          ))}
        </div>
      </div>
    );
  }

  /////////////////////////////////////////////////////////////////////////////////////////

  return (
    <div
      id="pane_home"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        paddingTop: "10px",
        paddingBottom: "10px",
        paddingRight: "10px",
      }}
    >
      <style>{style}</style>

      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: "var(--panel-border-radius)",
          boxShadow: "var(--panel-shadow)",
          background: "var(--emptyPanel-background)",
          display: "flex",
          flexDirection: "column",
          padding: "20px",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {/* <img
          id="emptyPaneAssistLogo"
          src="/EmptyPaneAssist/logo.png"
          style={{
            position: "relative",
            width: "200px",
          }}
        /> */}
        <OnBoardingElement />
        {/* <div
          id="dangerouslySetInnerHTML"
          style={{
            textAlign: "left",
            justifyContent: "flex-start",
          }}
          dangerouslySetInnerHTML={{ __html: content }}
        />

        {devs.length > 0 && <ListOfDevicesWithoutKnxProduct devices={devs} />}

        <OnBoardinCheckList /> */}

        {/* {isUiModeDeveloper() && (
          <button
            className="button"
            onClick={() => {
              state.activePanes.loadPage("ai");
            }}
          >
            erstelle mit hilfe von AI
          </button>
        )} */}
      </div>

      {/* <div style={{ display: "none" }}>{listOfDevicesWithoutKnxProduct}</div> */}
    </div>
  );
}
